/**
 * Goal 55A «risultati onesti» — what Step 1 checked, for this project.
 *
 * Every item has one state: compared with a list price and consistent, compared and with
 * differences, not compared (with the reason and the lists searched), or incomplete. This
 * panel shows, per state and per reason, the count, the known € and the rows whose amount
 * could not be read — over EVERY item, from the server's one implementation
 * (`tender-api analysis/checks.py`) — and the analysis marker with the checks it rests on.
 * Its job is to let a reader tell what the app knows from what it did not check: before it, an
 * item never compared with any list row looked exactly like a checked one.
 *
 * G3a adds the run's items account under the marker: the rows the run merged and the rows it
 * dropped, each with code, bill page and reason, and — when the items the scan found and the
 * items saved do not reconcile — how many and on which pages. An older run carries no account
 * and the panel shows nothing new.
 *
 * G3b adds two lines to that account: the starts the run set aside instead of sending them as
 * items («Inizi esclusi», each with page and reason) and the starts it read although their crop
 * prints no SOMMANO line («Inizi senza riga SOMMANO letti», with pages). Both are explained, so
 * neither is an alert; a run without the keys shows neither.
 */
import {
  billPagesWords,
  type AnalysisMarker,
  type CheckBucket,
  type CheckReason,
  type CheckState,
  type CheckSummary,
  type ItemsAccount,
  type ItemsAccountRow,
} from "../../lib/tenderApi";
import { fmtEurCents } from "../../lib/format";

export const CHECK_STATE_LABEL: Record<CheckState, string> = {
  controllata_coerente: "Controllata e coerente",
  controllata_con_differenze: "Controllata con differenze",
  non_controllata: "Non controllata",
  incompleta: "Incompleta",
};

/** The short form a table row has room for. */
export const CHECK_STATE_SHORT: Record<CheckState, string> = {
  controllata_coerente: "Coerente",
  controllata_con_differenze: "Con differenze",
  non_controllata: "Non controllata",
  incompleta: "Incompleta",
};

export const CHECK_REASON_LABEL: Record<CheckReason, string> = {
  nessuna_riga_listino: "Nessuna riga del listino per il codice",
  prezzo_listino_zero: "Prezzo di listino pari a zero (0,00 come stampato)",
  prezzo_listino_assente: "Prezzo di listino assente nella riga trovata",
  prezzo_listino_ambiguo: "Prezzo di listino in formato ambiguo (confronto non eseguito)",
  nessun_listino: "Nessun listino consultato",
  esito_non_registrato: "Esito del confronto non registrato",
};

const STATE_ORDER: CheckState[] = [
  "controllata_coerente",
  "controllata_con_differenze",
  "non_controllata",
  "incompleta",
];
const REASON_ORDER: CheckReason[] = [
  "nessuna_riga_listino",
  "prezzo_listino_zero",
  "prezzo_listino_assente",
  "prezzo_listino_ambiguo",
  "nessun_listino",
  "esito_non_registrato",
];

const STATE_COLOR: Record<CheckState, string> = {
  controllata_coerente: "var(--c-ok)",
  controllata_con_differenze: "var(--c-warn)",
  non_controllata: "var(--c-ink-3)",
  incompleta: "var(--c-err)",
};

function pct(value: number | null | undefined): string {
  if (value == null) return "non determinabile";
  return `${value.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function voci(n: number): string {
  return n === 1 ? "1 voce" : `${n.toLocaleString("it-IT")} voci`;
}

const MARKER_TONE: Record<AnalysisMarker["marker"], { fg: string; bg: string; border: string }> = {
  completa: { fg: "var(--c-ok)", bg: "var(--c-ok-soft)", border: "rgba(47,125,78,0.30)" },
  completezza_non_verificabile: {
    fg: "var(--c-warn)",
    bg: "var(--c-warn-soft)",
    border: "rgba(184,115,12,0.32)",
  },
  controlli_discordanti: {
    fg: "var(--c-err)",
    bg: "var(--c-err-soft)",
    border: "rgba(181,61,42,0.34)",
  },
  incompleta: { fg: "var(--c-err)", bg: "var(--c-err-soft)", border: "rgba(181,61,42,0.34)" },
};

const MARKER_TITLE: Record<AnalysisMarker["marker"], string> = {
  completa: "Analisi completa",
  incompleta: "Analisi incompleta",
  controlli_discordanti: "Controlli discordanti",
  completezza_non_verificabile: "Completezza non verificabile",
};

/** The two checks a marker rests on, in a reader's words. Mirrors `checks.check_verdict_words`. */
export function checkVerdicts(m: AnalysisMarker): { total: string; categories: string } {
  const t = m.checks.total;
  const c = m.checks.categories;
  let total: string;
  if (t.status === "ok") {
    total = `superato (somma delle voci ${fmtEurCents(t.extracted ?? 0)} = totale del documento ${fmtEurCents(t.stated ?? 0)})`;
  } else if (t.status === "under_extracted") {
    total = `le voci sommano meno del totale del documento (scarto ${fmtEurCents(t.residual ?? 0)}, ${t.residual_explained === true ? "spiegato" : "non spiegato"})`;
  } else if (t.status === "over_extracted") {
    const n = t.unread_amounts ?? 0;
    total =
      n > 0
        ? `le voci lette sommano più del totale del documento (scarto ${fmtEurCents(t.residual ?? 0)}; ${n === 1 ? "1 voce" : `${n} voci`} con importo non letto non compres${n === 1 ? "a" : "e"})`
        : `le voci sommano più del totale del documento (scarto ${fmtEurCents(t.residual ?? 0)})`;
  } else if (t.status === "unread_amounts") {
    const n = t.unread_amounts ?? 0;
    total = `non verificabile: ${n === 1 ? "1 voce" : `${n} voci`} con importo non letto (le voci lette sommano ${fmtEurCents(t.extracted ?? 0)}, il documento ${fmtEurCents(t.stated ?? 0)})`;
  } else if (t.status === "no_stated_total" || t.status === "unreadable") {
    total = "non eseguibile: il documento non riporta un totale leggibile";
  } else {
    total = "non registrato";
  }
  let categories: string;
  if (c.status === "ok") categories = "superato";
  else if (c.status === "mismatch") categories = "non torna";
  else if (c.status === "partial") categories = "parziale (un riepilogo non è leggibile)";
  else if (c.status === "unread_amounts") {
    const k = c.unread_categories ?? 0;
    categories = `non verificabile: importi non letti in ${k} ${k === 1 ? "categoria" : "categorie"}`;
  } else if (c.status == null) categories = "non registrato";
  else categories = "non applicabile (nessun riepilogo per categoria utilizzabile)";
  return { total, categories };
}

function isMarker(v: unknown): v is AnalysisMarker {
  if (!v || typeof v !== "object") return false;
  const o = v as Partial<AnalysisMarker>;
  return (
    typeof o.marker === "string" &&
    o.marker in MARKER_TITLE &&
    !!o.checks?.total &&
    !!o.checks?.categories &&
    Array.isArray(o.failed_items)
  );
}

/** The shape this panel reads; anything else is not rendered. */
function isCheckSummary(v: unknown): v is CheckSummary {
  if (!v || typeof v !== "object") return false;
  const o = v as Partial<CheckSummary>;
  return (
    typeof o.items === "number" &&
    !!o.states &&
    typeof o.states === "object" &&
    !!o.flags &&
    typeof o.flags.open === "number" &&
    typeof o.flags.resolved === "number" &&
    !!o.reasons
  );
}

function Bucket({ b }: { b: CheckBucket }): JSX.Element {
  return (
    <>
      <td className="t-num" style={{ textAlign: "right" }}>
        {b.count.toLocaleString("it-IT")}
      </td>
      <td className="t-num" style={{ textAlign: "right", fontWeight: 500 }}>
        {fmtEurCents(b.eur)}
      </td>
      <td className="t-num" style={{ textAlign: "right", color: "var(--c-ink-3)" }}>
        {pct(b.pct)}
      </td>
      <td className="t-num" style={{ textAlign: "right", color: "var(--c-ink-3)" }}>
        {b.no_amount.toLocaleString("it-IT")}
      </td>
    </>
  );
}

export function CheckSummaryPanel({
  summary,
  itemsAccount,
}: {
  summary: CheckSummary | undefined;
  /** G3a — `itemsAccountOf(job)`; null/absent for a run without an account. */
  itemsAccount?: ItemsAccount | null;
}): JSX.Element | null {
  // Nothing rather than a crash: a server without this endpoint, or a payload of another
  // shape, must not take the whole Audit page down with it. The items account does not
  // depend on this endpoint, so a found ≠ saved difference is still said without it.
  if (!isCheckSummary(summary)) return <ItemsAccountBlock account={itemsAccount} />;
  // A marker the server could not build arrives without a known `marker` (review, F3):
  // the panel says it is not recorded instead of throwing.
  const m = isMarker(summary.marker) ? summary.marker : null;
  const tone = m ? MARKER_TONE[m.marker] : null;
  const verdicts = m ? checkVerdicts(m) : null;
  return (
    <section
      data-testid="check-summary"
      style={{
        marginBottom: 16,
        padding: "12px 14px",
        borderRadius: 8,
        border: "1px solid var(--c-line)",
        background: "var(--c-bg-elev)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            Controllo delle voci sul listino di riferimento
          </div>
          <div style={{ fontSize: 12, color: "var(--c-ink-2)", marginTop: 2 }}>
            Calcolato su tutte le {voci(summary.items)}, su{" "}
            {summary.denominator_eur == null
              ? "nessun importo letto"
              : `${fmtEurCents(summary.denominator_eur)} di importi letti`}
            : ogni percentuale è di questa somma.
          </div>
        </div>
        <div data-testid="flag-counts" style={{ fontSize: 12.5, color: "var(--c-ink-2)" }}>
          Segnalazioni: <strong>{summary.flags.open.toLocaleString("it-IT")} aperte</strong> ·{" "}
          {summary.flags.resolved.toLocaleString("it-IT")} risolte
        </div>
      </div>

      <table className="table" style={{ marginTop: 10, fontSize: 12.5, width: "100%" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Stato</th>
            <th style={{ textAlign: "right" }}>Voci</th>
            <th style={{ textAlign: "right" }}>Importo noto</th>
            <th style={{ textAlign: "right" }}>Quota</th>
            <th style={{ textAlign: "right" }}>Importo non determinabile</th>
          </tr>
        </thead>
        <tbody>
          {STATE_ORDER.map((state) => (
            <StateRows key={state} state={state} summary={summary} />
          ))}
        </tbody>
      </table>

      {m && tone && verdicts && (
        <div
          data-testid="check-marker"
          data-marker={m.marker}
          role={m.marker === "completa" ? "status" : "alert"}
          style={{
            marginTop: 10,
            padding: "9px 12px",
            borderRadius: 8,
            background: tone.bg,
            border: `1px solid ${tone.border}`,
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 600, color: tone.fg }}>{MARKER_TITLE[m.marker]}</div>
          <div>Controllo del totale: {verdicts.total}.</div>
          <div>Controllo per categoria: {verdicts.categories}.</div>
          <div>
            Voci non lette dall&apos;estrazione:{" "}
            {/* Correction pass of 4 Oct, item 3: a missing record is an UNKNOWN count. */}
            {m.failed_items_recorded === false
              ? "numero sconosciuto (registro non disponibile per l’estrazione usata)"
              : m.failed_items.length === 0
                ? "nessuna"
                : m.failed_items.map((f) => f.id ?? "?").join(", ")}
            . Voci incomplete: {m.incomplete_items}.
            {m.failed_items_source?.job_id && (
              <span style={{ color: "var(--c-ink-3)" }}>
                {" "}
                Registro: analisi {m.failed_items_source.job_id.slice(0, 8)}.
              </span>
            )}
          </div>
          {/* Correction pass of 4 Oct, item 2: the reasons of the verdict — the ones the
              chatbot's block prints for the same analysis. */}
          {(m.reasons ?? []).length > 0 && (
            <ul data-testid="check-marker-reasons" style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {(m.reasons ?? []).map((why) => (
                <li key={why}>{why}</li>
              ))}
            </ul>
          )}
          {m.marker === "completa" && (
            <div style={{ color: "var(--c-ink-3)" }}>
              Un totale che torna dice che non mancano importi, non che ogni voce sia corretta.
            </div>
          )}
        </div>
      )}
      {!m && (
        <div
          data-testid="check-marker-missing"
          style={{ marginTop: 10, fontSize: 12.5, color: "var(--c-ink-3)" }}
        >
          Completezza dell&apos;analisi: non registrata.
        </div>
      )}
      <ItemsAccountBlock account={itemsAccount} />
    </section>
  );
}

/** G3a — merges, drops and found ≠ saved; G3b — starts set aside and starts read without a
 *  SOMMANO line. Nothing when there is nothing to report. */
function ItemsAccountBlock({
  account,
}: {
  account: ItemsAccount | null | undefined;
}): JSX.Element | null {
  if (!account) return null;
  const { merges, drops, unexplained } = account;
  const setAside = account.setAside ?? [];
  const unclosed = account.unclosedRead ?? { count: 0, pages: [] };
  if (
    merges.length === 0 &&
    drops.length === 0 &&
    unexplained.count <= 0 &&
    setAside.length === 0 &&
    unclosed.count <= 0
  )
    return null;
  const pages = billPagesWords(unexplained.pages);
  const unclosedPages = billPagesWords(unclosed.pages);
  return (
    <div style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.5 }}>
      {unexplained.count > 0 && (
        <div
          data-testid="items-account-unexplained"
          role="alert"
          style={{ color: "var(--c-err)", fontWeight: 600 }}
        >
          {`Voci trovate e voci salvate non corrispondono: ${unexplained.count.toLocaleString("it-IT")}${pages ? ` (${pages})` : ""}`}
        </div>
      )}
      {merges.length > 0 && (
        <AccountRows testId="items-account-merges" title="Righe unite" rows={merges} />
      )}
      {drops.length > 0 && (
        <AccountRows testId="items-account-drops" title="Righe scartate" rows={drops} />
      )}
      {setAside.length > 0 && (
        <div data-testid="items-account-set-aside" style={{ marginTop: 4 }}>
          <div style={{ fontWeight: 600 }}>
            {`Inizi esclusi: ${setAside.length.toLocaleString("it-IT")}`}
          </div>
          <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
            {setAside.map((s, k) => (
              <li key={k}>
                {[billPagesWords(s.pages) || "pagina non registrata", s.reason]
                  .filter(Boolean)
                  .join(" — ")}
              </li>
            ))}
          </ul>
        </div>
      )}
      {unclosed.count > 0 && (
        <div data-testid="items-account-unclosed" style={{ marginTop: 4, fontWeight: 600 }}>
          {`Inizi senza riga SOMMANO letti: ${unclosed.count.toLocaleString("it-IT")}${unclosedPages ? ` (${unclosedPages})` : ""}`}
        </div>
      )}
    </div>
  );
}

function AccountRows({
  testId,
  title,
  rows,
}: {
  testId: string;
  title: string;
  rows: ItemsAccountRow[];
}): JSX.Element {
  return (
    <div data-testid={testId} style={{ marginTop: 4 }}>
      <div style={{ fontWeight: 600 }}>{`${title}: ${rows.length.toLocaleString("it-IT")}`}</div>
      <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
        {rows.map((r, k) => (
          <li key={k}>
            {[r.code || "—", billPagesWords(r.pages) || "pagina non registrata", r.reason]
              .filter(Boolean)
              .join(" — ")}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StateRows({ state, summary }: { state: CheckState; summary: CheckSummary }): JSX.Element {
  const b = summary.states[state] ?? { count: 0, eur: 0, no_amount: 0, pct: null };
  const reasons = state === "non_controllata" ? REASON_ORDER.filter((r) => summary.reasons[r]) : [];
  return (
    <>
      <tr data-testid={`check-state-${state}`}>
        <td>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              aria-hidden
              style={{ width: 8, height: 8, borderRadius: 4, background: STATE_COLOR[state] }}
            />
            {CHECK_STATE_LABEL[state]}
          </span>
        </td>
        <Bucket b={b} />
      </tr>
      {reasons.map((r) => {
        const rb = summary.reasons[r]!;
        return (
          <tr key={r} data-testid={`check-reason-${r}`} style={{ color: "var(--c-ink-2)" }}>
            <td style={{ paddingLeft: 22 }}>
              {CHECK_REASON_LABEL[r]}
              {rb.lists && rb.lists.length > 0 && (
                <span style={{ color: "var(--c-ink-3)" }}>
                  {" "}
                  — listini consultati: {rb.lists.join(", ")}
                </span>
              )}
            </td>
            <Bucket b={rb} />
          </tr>
        );
      })}
    </>
  );
}
